import { ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import jaJP from 'antd/lib/locale/ja_JP';
import axios from 'axios';
import LoadingComponent from 'components/Loading';
import { viewPdfFile } from 'helpers/file';
import { authoritySelector } from 'modules/auth/selectors';
import { getAuthority } from 'modules/auth/slice';
import { getSignedUrlSelector, pdfDownloadSelector } from 'modules/common/selectors';
import { clearSignedUrl, setPdfDownload } from 'modules/common/slice';
import { PublicLayout, PrivateLayout } from 'pages/layout';
import NotFound from 'pages/notFound';
import React, { Suspense, useState, useEffect } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { Route, Router, Switch } from 'react-router-dom';
import { privateRoutes, publicRoutes } from 'Routes';
import { convertAuthorityMenu } from 'utils';
import { useAppDispatch, useAppSelector } from 'utils/customHooks';
import { browserHistory } from 'utils/history';

import 'antd/dist/antd.css';
import './App.css';
import './sass/_app.scss';

function App() {
  const { t, i18n } = useTranslation();
  const authority = useAppSelector(authoritySelector);
  const signedUrl = useAppSelector(getSignedUrlSelector);
  const pdfDownload = useAppSelector(pdfDownloadSelector);
  const [locale, setLocale] = useState<any>('en');
  const [router, setRouter] = useState<any>([]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const routerAuthority = convertAuthorityMenu(privateRoutes(t), authority);
    setRouter([{ ...routerAuthority[0], path: '/', inMenu: false, subMenu: false }, ...routerAuthority]);
  }, [authority]);

  useEffect(() => {
    dispatch(getAuthority());
  }, []);

  useEffect(() => {
    switch (i18n.language) {
      case 'en':
        setLocale(enUS);
        break;

      case 'jp':
        setLocale(jaJP);
        break;

      default:
        setLocale(enUS);
        break;
    }
  }, [i18n.language]);

  // 署名付きURL(CloudFront)から取得したファイルのダウンロード処理
  useEffect(() => {
    if (signedUrl) {
      axios({
        url: signedUrl,
        method: 'GET',
        responseType: 'blob',
      }).then((response) => {
        if (pdfDownload) {
          // PDFプレビュー
          viewPdfFile(signedUrl);
        } else {
          // レスポンスからデータを取得
          const file = new Blob([response.data], { type: 'application/octet-stream' });
          // ブラウザでファイルをダウンロード
          const fileURL = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = fileURL;

          // URLからファイル名を抽出
          const fileNameRegex = /\/([^/]+)\?Expires=/;
          const match = signedUrl.match(fileNameRegex);
          if (match && match[1]) {
            link.setAttribute('download', decodeURIComponent(match[1]));
          } else {
            // 取得出来なかった場合は既定のファイル名
            link.setAttribute('download', 'NoNameFile');
          }
          document.body.appendChild(link);
          link.click();
        }
        // 署名付きURLをクリアする
        dispatch(clearSignedUrl());
        // PDFプレビューフラグをクリアする
        dispatch(setPdfDownload(false));
      });
    }
  }, [signedUrl]);

  return (
    <div className="App bg-app">
      <ConfigProvider autoInsertSpaceInButton={false} locale={locale}>
        <Router history={browserHistory}>
          <Suspense fallback={<LoadingComponent />}>
            <Switch>
              <Route path={publicRoutes.map((route) => route.path)} component={PublicLayout} />
              <Route path={[...router?.map((route: any) => route?.path)]} component={PrivateLayout} />
              <Route path="*" component={NotFound} />
            </Switch>
          </Suspense>
        </Router>
      </ConfigProvider>
    </div>
  );
}

export default withTranslation()(App);
